import { Injectable, inject } from '@angular/core';
import {ActivatedRouteSnapshot, Router, RouterStateSnapshot} from '@angular/router';
import {EMPTY, Observable} from 'rxjs';
import {select, Store} from '@ngrx/store';
import {AppState} from '../store';
import {watchLesson} from '../store/lesson.actions';
import {selectActiveCourse, selectActiveCourseAllPublishedLessons, selectActiveCourseSections} from '../store/selectors';
import {catchError, filter, first, map, switchMap, tap, timeout, withLatestFrom} from 'rxjs/operators';
import {selectAllQuestionsPaginationInfo} from '../store/questions.selectors';
import {loadLessonQuestionsPage} from '../store/questions.actions';
import {loadActiveCourseSection} from '../store/course.actions';


@Injectable()
export class ActiveLessonResolver  {
  private store = inject<Store<AppState>>(Store);
  private router = inject(Router);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);


  constructor() {

  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
    const sectionSeqNo = parseInt(route.paramMap.get('sectionSeqNo'));
    const lessonSeqNo = parseInt(route.paramMap.get('lessonSeqNo'));
    const courseUrl = route.paramMap.get('courseUrl');

    if (isNaN(sectionSeqNo) || isNaN(lessonSeqNo)) {
      this.router.navigate(courseUrl ? ['/courses', courseUrl] : ['/courses']);
      return EMPTY;
    }

    return this.store.pipe(
      select(selectActiveCourse),
      filter(c => !!c),
      first(),
      tap(course => this.store.dispatch(loadActiveCourseSection({ courseId: course.id, sectionSeqNo }))),
      switchMap(() => this.store.pipe(
        select(selectActiveCourseAllPublishedLessons),
        withLatestFrom(this.store.pipe(select(selectActiveCourseSections))),
        filter(([lessons, sections]) => sections.some(s => s.seqNo == sectionSeqNo)),
        map(([lessons, sections]) => {
          const activeSection = sections.find(section => section.seqNo == sectionSeqNo);
          const activeSectionLessons = lessons.filter(lesson => lesson.sectionId == activeSection.id);
          return activeSectionLessons.find(lesson => lesson.seqNo == lessonSeqNo);
        }),
        filter(lesson => !!lesson),
        tap(lesson => this.store.dispatch(watchLesson({ lessonId: lesson.id }))),
        withLatestFrom(
          this.store.pipe(select(selectActiveCourse)),
          this.store.pipe(select(selectAllQuestionsPaginationInfo))
        ),
        tap(([lesson, activeCourse, paginationInfo]) => {
          if (!paginationInfo[lesson.id]) {
            this.store.dispatch(loadLessonQuestionsPage({ lessonId: lesson.id, courseId: activeCourse.id, lastTimestampLoaded: 0 }));
          }
        }),
        first()
      )),
      timeout(10000),
      catchError(() => {
        this.router.navigate(courseUrl ? ['/courses', courseUrl] : ['/courses']);
        return EMPTY;
      })
    );
  }


}
